import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Form,
  ListGroup,
  Nav,
  Spinner,
  Tab
} from 'react-bootstrap';
import medicalExamCatalogService from '../../services/medicalExamCatalogService';
import consultationExamOrderService from '../../services/consultationExamOrderService';
import {
  ANALYSIS_FAMILIES,
  MEDICAL_COVERAGE_TYPES,
  MEDICAL_EXAM_CATEGORIES,
  getMedicalExamCategoryConfig
} from '../../constants/medicalExamCategories';
import {
  examMetaLine,
  getClinicalHint,
  getClinicalInformationForCategory,
  getClinicalPlaceholder,
  getTabEmptyMessage,
  getViewPrintLabel,
  parseExamOrdersResponse
} from './utils/examOrderFormat';
import ConsultationExamOrdersViewModal from './ConsultationExamOrdersViewModal';
import { ClinicalButton, ClinicalButtonStack } from './ui';
import './ConsultationExamOrdersPanel.css';

const TAB_ANALYSIS = MEDICAL_EXAM_CATEGORIES.ANALYSIS.id;
const TAB_RADIO = MEDICAL_EXAM_CATEGORIES.RADIOGRAPHY.id;
const TAB_ULTRA = MEDICAL_EXAM_CATEGORIES.ULTRASOUND.id;

const EXAM_TABS = [TAB_ANALYSIS, TAB_RADIO, TAB_ULTRA];

function normalizeCategory(category) {
  return String(category || '').toUpperCase();
}

function matchesCategory(lineCategory, tabCategory) {
  return normalizeCategory(lineCategory) === normalizeCategory(tabCategory);
}

/**
 * Demandes d'analyses, radiographies et échographies liées à une consultation (catalogue institutionnel).
 */
export default function ConsultationExamOrdersPanel({ consultation, onSuccess, readOnly = false }) {
  const cid = consultation?.id;
  const [activeTab, setActiveTab] = useState(TAB_ANALYSIS);
  const [coverageType, setCoverageType] = useState(MEDICAL_COVERAGE_TYPES.SMIE.id);
  const [analysisFamily, setAnalysisFamily] = useState('');
  const [search, setSearch] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [lines, setLines] = useState([]);
  const [clinicalInformationAnalysis, setClinicalInformationAnalysis] = useState('');
  const [clinicalInformationRadiography, setClinicalInformationRadiography] = useState('');
  const [clinicalInformationUltrasound, setClinicalInformationUltrasound] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewOrders, setViewOrders] = useState([]);
  const [viewCategory, setViewCategory] = useState(TAB_ANALYSIS);
  const [viewClinicalInformation, setViewClinicalInformation] = useState('');
  const [viewPreviewUnsaved, setViewPreviewUnsaved] = useState(false);
  const [loadingView, setLoadingView] = useState(false);

  const loadOrders = useCallback(async () => {
    if (!cid) return;
    setLoadingOrders(true);
    setError('');
    try {
      const res = await consultationExamOrderService.list(cid);
      if (res.success) {
        const parsed = parseExamOrdersResponse(res);
        setLines(parsed.orders);
        setClinicalInformationAnalysis(parsed.clinical_information_analysis);
        setClinicalInformationRadiography(parsed.clinical_information_radiography);
        setClinicalInformationUltrasound(parsed.clinical_information_ultrasound);
        setDirty(false);
      }
    } catch (e) {
      setError(e.message || 'Chargement des demandes impossible.');
    } finally {
      setLoadingOrders(false);
    }
  }, [cid]);

  useEffect(() => {
    if (!cid) return;
    setLines([]);
    setClinicalInformationAnalysis('');
    setClinicalInformationRadiography('');
    setClinicalInformationUltrasound('');
    setSearch('');
    setDirty(false);
    loadOrders();
  }, [cid, loadOrders]);

  useEffect(() => {
    if (!cid) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingCatalog(true);
      try {
        const params = {
          category: activeTab,
          coverage_type: coverageType,
          is_active: 'true'
        };
        if (activeTab === TAB_ANALYSIS && analysisFamily) {
          params.analysis_family = analysisFamily;
        }
        if (search.trim()) params.search = search.trim();
        const res = await medicalExamCatalogService.getExams(params);
        if (!cancelled && res.success) {
          setCatalog(Array.isArray(res.data) ? res.data : []);
        } else if (!cancelled) {
          setCatalog([]);
        }
      } catch {
        if (!cancelled) setCatalog([]);
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cid, activeTab, coverageType, analysisFamily, search]);

  const linesForTab = useMemo(
    () => lines.filter((l) => matchesCategory(l.category, activeTab)),
    [lines, activeTab]
  );

  const selectedIds = useMemo(
    () => new Set(linesForTab.map((l) => l.catalog_exam_id)),
    [linesForTab]
  );

  const addExam = (exam) => {
    const category = normalizeCategory(exam.category || activeTab);
    if (lines.some((l) => l.catalog_exam_id === exam.id)) return;
    setLines((prev) => [
      ...prev,
      {
        catalog_exam_id: exam.id,
        exam_name: exam.name,
        category,
        coverage_type: exam.coverage_type,
        analysis_family: exam.analysis_family,
        metadata: exam.metadata || {}
      }
    ]);
    setDirty(true);
    setError('');
  };

  const removeLine = (catalogExamId) => {
    setLines((prev) => prev.filter((l) => l.catalog_exam_id !== catalogExamId));
    setDirty(true);
  };

  const handleOpenView = async () => {
    const tabLines = lines.filter((l) => matchesCategory(l.category, activeTab));
    if (tabLines.length === 0) {
      setError(getTabEmptyMessage(activeTab));
      return;
    }
    setLoadingView(true);
    setError('');
    try {
      setViewCategory(activeTab);
      if (dirty) {
        setViewOrders(tabLines);
        setViewClinicalInformation(getClinicalInformationForCategory(
          {
            clinical_information_analysis: clinicalInformationAnalysis,
            clinical_information_radiography: clinicalInformationRadiography,
            clinical_information_ultrasound: clinicalInformationUltrasound
          },
          activeTab
        ));
        setViewPreviewUnsaved(true);
        setShowViewModal(true);
        return;
      }
      const res = await consultationExamOrderService.list(cid);
      if (res.success) {
        const parsed = parseExamOrdersResponse(res);
        const filtered = parsed.orders.filter((o) => matchesCategory(o.category, activeTab));
        if (filtered.length === 0) {
          setError(getTabEmptyMessage(activeTab, true));
          return;
        }
        setViewOrders(filtered);
        setViewClinicalInformation(getClinicalInformationForCategory(parsed, activeTab));
        setViewPreviewUnsaved(false);
        setShowViewModal(true);
      } else {
        setError(res.message || 'Impossible de charger les demandes.');
      }
    } catch (e) {
      setError(e.message || 'Impossible d\'ouvrir l\'aperçu.');
    } finally {
      setLoadingView(false);
    }
  };

  const handleSave = async () => {
    if (!cid) return;
    setError('');
    setBusy(true);
    try {
      const payload = lines.map((l) => ({
        catalog_exam_id: l.catalog_exam_id
      }));
      const res = await consultationExamOrderService.sync(
        cid,
        payload,
        clinicalInformationAnalysis.trim() || null,
        clinicalInformationRadiography.trim() || null,
        clinicalInformationUltrasound.trim() || null
      );
      if (!res.success) {
        throw new Error(res.message || 'Enregistrement refusé.');
      }
      const parsed = parseExamOrdersResponse(res);
      setLines(parsed.orders);
      setClinicalInformationAnalysis(parsed.clinical_information_analysis);
      setClinicalInformationRadiography(parsed.clinical_information_radiography);
      setClinicalInformationUltrasound(parsed.clinical_information_ultrasound);
      setDirty(false);
      onSuccess?.();
    } catch (e) {
      setError(e.message || 'Enregistrement des demandes impossible.');
    } finally {
      setBusy(false);
    }
  };

  if (!cid) return null;

  const tabConfig = getMedicalExamCategoryConfig(activeTab);
  const clinicalStateByTab = {
    [TAB_ANALYSIS]: [clinicalInformationAnalysis, setClinicalInformationAnalysis],
    [TAB_RADIO]: [clinicalInformationRadiography, setClinicalInformationRadiography],
    [TAB_ULTRA]: [clinicalInformationUltrasound, setClinicalInformationUltrasound]
  };
  const [activeClinicalValue, setActiveClinicalValue] =
    clinicalStateByTab[activeTab] || clinicalStateByTab[TAB_ANALYSIS];

  return (
    <div className={`exam-orders-panel${readOnly ? ' exam-orders-panel--readonly' : ''}`}>
      <p className="exam-orders-panel__intro">
        {readOnly
          ? 'Consultation terminée — consultation et impression des bons d’examens.'
          : 'Choisissez les examens du catalogue (onglet actif), filtrez SMIE / CMC si besoin, puis enregistrez.'}
      </p>

      {error ? (
        <Alert variant="danger" className="py-2 small mb-2">
          {error}
        </Alert>
      ) : null}

      {loadingOrders ? (
        <div className="text-center py-2">
          <Spinner animation="border" size="sm" />
        </div>
      ) : null}

      <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
        <Nav variant="tabs" className="exam-orders-panel__tabs mb-2" role="tablist">
          {EXAM_TABS.map((tabId) => (
            <Nav.Item key={tabId}>
              <Nav.Link eventKey={tabId}>
                {getMedicalExamCategoryConfig(tabId)?.labelPlural}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        <Tab.Content>
          <div className="mb-2">
            <div className="exam-orders-panel__filters d-flex flex-wrap gap-1 mb-2">
              {Object.values(MEDICAL_COVERAGE_TYPES).map((cov) => (
                <ClinicalButton
                  key={cov.id}
                  compact
                  variant={coverageType === cov.id ? 'primary' : 'secondary'}
                  type="button"
                  onClick={() => setCoverageType(cov.id)}
                  disabled={busy}
                >
                  {cov.label}
                </ClinicalButton>
              ))}
            </div>

            {activeTab === TAB_ANALYSIS ? (
              <Form.Select
                size="sm"
                className="mb-2"
                value={analysisFamily}
                onChange={(e) => setAnalysisFamily(e.target.value)}
                disabled={busy}
                aria-label="Famille d'analyse"
              >
                <option value="">Toutes les familles</option>
                {Object.values(ANALYSIS_FAMILIES).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </Form.Select>
            ) : null}

            <Form.Control
              size="sm"
              type="search"
              placeholder={`Rechercher dans le catalogue ${tabConfig?.labelPlural?.toLowerCase() || ''}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className="exam-orders-panel__catalog border rounded mb-3">
            {loadingCatalog ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : catalog.length === 0 ? (
              <p className="small text-muted mb-0 p-3">Aucun examen trouvé pour ces filtres.</p>
            ) : (
              <ListGroup variant="flush">
                {catalog.map((exam) => {
                  const added = selectedIds.has(exam.id);
                  const meta = examMetaLine(exam);
                  return (
                    <ListGroup.Item
                      key={exam.id}
                      action={!added}
                      disabled={added || busy}
                      className={`exam-orders-panel__catalog-item py-2 ${added ? 'is-added' : ''}`}
                      onClick={() => !added && addExam(exam)}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <strong className="small">{exam.name}</strong>
                          {meta ? (
                            <div className="exam-orders-panel__meta text-muted">{meta}</div>
                          ) : null}
                          {exam.description ? (
                            <div className="exam-orders-panel__meta text-muted">{exam.description}</div>
                          ) : null}
                        </div>
                        {added ? (
                          <Badge bg="secondary" className="fw-normal">
                            Ajouté
                          </Badge>
                        ) : (
                          <span className="small text-primary">+ Ajouter</span>
                        )}
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}
          </div>

          {linesForTab.length > 0 ? (
            <div className="mb-3">
              <div className="small fw-semibold text-muted mb-2">
                Demandes — {tabConfig?.labelPlural} ({linesForTab.length})
              </div>
              <ListGroup variant="flush" className="exam-orders-panel__demand-list border rounded">
                {linesForTab.map((line) => (
                  <ListGroup.Item key={line.catalog_exam_id} className="py-2">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div className="small">
                        <strong>{line.exam_name}</strong>
                        {examMetaLine(line) ? (
                          <div className="exam-orders-panel__meta text-muted">
                            {examMetaLine(line)}
                          </div>
                        ) : null}
                      </div>
                      <ClinicalButton
                        variant="danger"
                        compact
                        type="button"
                        disabled={busy}
                        onClick={() => removeLine(line.catalog_exam_id)}
                      >
                        Retirer
                      </ClinicalButton>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          ) : (
            <p className="small text-muted mb-3">
              Aucune demande pour cet onglet — parcourez le catalogue ci-dessus.
            </p>
          )}

          <Form.Group className="exam-orders-panel__clinical mb-0 mt-3">
            <Form.Label className="small fw-semibold">Renseignement clinique</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              size="sm"
              placeholder={getClinicalPlaceholder(activeTab)}
              value={activeClinicalValue}
              onChange={(e) => {
                setActiveClinicalValue(e.target.value);
                setDirty(true);
              }}
              disabled={busy}
            />
            <Form.Text muted className="small">
              {getClinicalHint(activeTab)}
            </Form.Text>
          </Form.Group>
        </Tab.Content>
      </Tab.Container>

      {lines.length > 0 ? (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <Badge bg="light" text="dark" className="border fw-normal">
            Total : {lines.length} examen{lines.length > 1 ? 's' : ''}
          </Badge>
          {dirty ? (
            <span className="small text-warning">Modifications non enregistrées</span>
          ) : null}
        </div>
      ) : null}

      <ClinicalButtonStack>
        <ClinicalButton
          variant="view"
          icon="eye"
          block
          loading={loadingView}
          disabled={busy || linesForTab.length === 0}
          onClick={handleOpenView}
        >
          Voir &amp; imprimer {getViewPrintLabel(activeTab)}
        </ClinicalButton>

        {!readOnly ? (
          <ClinicalButton
            variant="save"
            block
            loading={busy}
            disabled={!dirty}
            onClick={handleSave}
          >
            Enregistrer les demandes d&apos;examens
          </ClinicalButton>
        ) : null}
      </ClinicalButtonStack>

      <ConsultationExamOrdersViewModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        consultation={consultation}
        orders={viewOrders}
        category={viewCategory}
        clinicalInformation={viewClinicalInformation}
        previewUnsaved={viewPreviewUnsaved}
      />
    </div>
  );
}
